import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import HistoricalDataAnalysisIndicator from './HistoricalDataAnalysisIndicator';

// Helper function to map tool names to friendly display names
const getFriendlyToolName = (toolName) => {
  const toolMapping = {
    'launch_llm_mode_test': 'AI Playground',
    'navigate_to_analysis': 'Analysis Navigation',
    'navigate_to_page': 'Page Analysis',
    'navigate_to_prompt_burst': 'Prompt Burst',
    'auto_run_prompt_burst': 'Auto Run Prompt Burst',
    'navigate_to_ai_crawler': 'AI Crawler',
    'auto_run_ai_crawler': 'Auto Run AI Crawler',
    'trigger_analysis': 'Traffic Analysis',
    'highlight_table_rows': 'Data Highlighting',
    'create_tutorial': 'Tutorial Generator',
    'present_tutorial_cards': 'Tutorial Options',
    'present_choice_cards': 'Choice Cards',
    'get_search_console_summary': 'Search Console Data',
    'analyze_page_structure': 'Page Structure Analysis'
  };
  
  return toolMapping[toolName] || toolName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to check if message should be truncated and get truncated version
const getMessageDisplayContent = (content, maxLength = 800, portalMode = false, currentContext = null) => {
  // In portal mode or tutorial mode, never truncate messages
  if (portalMode || (currentContext?.type === 'tutorial_interaction') || !content || content.length <= maxLength) {
    return { content, isTruncated: false };
  }
  
  // Find a good break point (end of sentence, paragraph, or word)
  const truncateLength = maxLength;
  let truncatedContent = content.substring(0, truncateLength);
  
  // Try to break at end of sentence
  const lastSentenceEnd = Math.max(
    truncatedContent.lastIndexOf('.'),
    truncatedContent.lastIndexOf('!'),
    truncatedContent.lastIndexOf('?')
  );
  
  if (lastSentenceEnd > truncateLength * 0.6) {
    truncatedContent = content.substring(0, lastSentenceEnd + 1);
  } else {
    // Try to break at end of word
    const lastSpace = truncatedContent.lastIndexOf(' ');
    if (lastSpace > truncateLength * 0.6) {
      truncatedContent = content.substring(0, lastSpace);
    }
    truncatedContent += '...';
  }
  
  return { content: truncatedContent, isTruncated: true };
};

// ChatSidebar-specific MessageBubble component with purple/pink gradient theme
// and hidden message functionality - extracted from ChatSidebar.jsx
const ChatSidebarMessageBubble = ({ message, darkMode, onSuggestionClick, onExpandMessage, onInlineButtonClick, isAutoExpanding = false, currentDomain = null, portalMode = false, currentContext = null }) => {
    const isUser = message.type === 'user';
    const navigate = useNavigate();
      
    // Hide messages marked as hidden (for silent prompts)
    if (message.isHidden) {
      return null;
    }
    
    // Hide onboarding assessment messages when loaded from conversation history
    if (message.type === 'user' && message.content && message.content.includes('conduct initial assessment after new user onboard flow')) {
      return null;
    }
    
    return (
      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[90%] ${isUser ? 'order-2' : 'order-1'}`}>
          {/* Message bubble */}
          <div 
            className={`
              rounded-2xl px-4 py-3 text-sm shadow-sm relative group transition-all duration-200
              ${(() => {
                // Check if this message should be truncated
                const { isTruncated } = getMessageDisplayContent(message.content || '', isUser ? 600 : 800, portalMode, currentContext);
                const pulseClass = isTruncated && !isUser ? 'animate-pulse-subtle' : '';
                
                const baseClasses = isUser 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                  : message.isError
                    ? darkMode
                      ? 'bg-red-900 text-red-100 border border-red-700'
                      : 'bg-red-50 text-red-900 border border-red-200'
                    : darkMode 
                      ? 'bg-gray-800 text-gray-100 border border-gray-700 hover:bg-gray-700 hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-500/10' 
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-50 hover:border-blue-300/50 hover:shadow-lg hover:shadow-blue-400/10 border border-transparent';
                
                return `${baseClasses} ${pulseClass}`;
              })()}
              ${!isUser && onExpandMessage && !message.disableExpansion && !message.showInlineButtons ? 'cursor-pointer' : ''}
            `}
            {...(!isUser && { 'data-role': 'assistant' })} // Add data attribute for sparkle trail
            {...(!isUser && onExpandMessage && !message.disableExpansion && !message.showInlineButtons && { 
              onClick: () => onExpandMessage(message),
              title: 'Click to expand message'
            })}
          >
            {/* Hover expand icon - only show for non-user messages without inline buttons */}
            {!isUser && onExpandMessage && !message.disableExpansion && !message.showInlineButtons && (
              <div className={`
                absolute top-2 right-2 opacity-0 group-hover:opacity-70 transition-all duration-200 pointer-events-none
                ${darkMode ? 'text-blue-400' : 'text-blue-600'}
              `}>
                <svg className="w-4 h-4 transform group-hover:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </div>
            )}
            
  
            {/* Context indicator */}
            {message.contextDetected && (
              <div className={`
                text-xs mb-2 px-2 py-1 rounded
                ${darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-50 text-blue-700'}
              `}>
                📍 {message.contextDetected}
              </div>
            )}
            
            {/* Tools used indicator */}
            {message.toolsUsed && Array.isArray(message.toolsUsed) && message.toolsUsed.length > 0 && (
              <div className={`
                text-xs mb-1 flex items-center gap-1
                ${darkMode ? 'text-gray-400' : 'text-gray-500'}
              `}>
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                {message.toolsUsed.map(tool => getFriendlyToolName(tool)).join(', ')}
              </div>
            )}
            
            <div>
              {/* Custom component for historical data analysis */}
              {!isUser && message.context?.type === 'onboarding_historical_analysis' ? (
                <HistoricalDataAnalysisIndicator darkMode={darkMode} />
              ) : isUser ? (
                (() => {
                  const { content: displayContent, isTruncated } = getMessageDisplayContent(message.content, 600, portalMode, currentContext);
                  return (
                    <div>
                      <div className="whitespace-pre-wrap break-words overflow-wrap-anywhere">
                        {displayContent}
                      </div>
                      {isTruncated && !message.disableExpansion && !message.showInlineButtons && (
                        <button
                          onClick={() => onExpandMessage(message)}
                          className={`mt-2 text-xs font-medium hover:underline ${
                            darkMode ? 'text-purple-200 hover:text-purple-100' : 'text-purple-600 hover:text-purple-700'
                          }`}
                        >
                          📖 Click to read full message
                        </button>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className={`prose prose-sm max-w-none break-words overflow-hidden ${
                  darkMode 
                    ? 'prose-invert prose-headings:text-gray-100 prose-p:text-gray-200 prose-strong:text-gray-100 prose-em:text-gray-300 prose-code:text-gray-200 prose-code:bg-gray-700 prose-pre:bg-gray-800 prose-pre:overflow-x-auto prose-pre:max-w-full prose-blockquote:text-gray-300 prose-blockquote:border-gray-600' 
                    : 'prose-headings:text-gray-900 prose-p:text-gray-800 prose-strong:text-gray-900 prose-em:text-gray-700 prose-code:text-gray-800 prose-code:bg-gray-100 prose-pre:bg-gray-50 prose-pre:overflow-x-auto prose-pre:max-w-full prose-blockquote:text-gray-700 prose-blockquote:border-gray-300'
                }`}>
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                    // Custom components for better styling
                    h1: ({ children }) => <h1 className="text-base font-bold mb-2">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-sm font-bold mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
                    p: ({ children }) => <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>,
                    ul: ({ children }) => <ul className="mb-3 pl-4 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="mb-3 pl-4 space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="text-sm break-words overflow-wrap-anywhere" style={{ overflowWrap: 'anywhere' }}>{children}</li>,
                    code: ({ children, className }) => {
                      const isInline = !className;
                      return isInline ? (
                        <code className={`px-1 py-0.5 rounded text-xs font-mono break-words overflow-wrap-anywhere ${
                          darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                        }`} style={{ overflowWrap: 'anywhere' }}>
                          {children}
                        </code>
                      ) : (
                        <pre className={`p-2 rounded text-xs font-mono overflow-x-auto max-w-full ${
                          darkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-50 text-gray-800'
                        }`}>
                          <code className="block whitespace-pre-wrap break-words" style={{ overflowWrap: 'anywhere' }}>
                            {children}
                          </code>
                        </pre>
                      );
                    },
                    blockquote: ({ children }) => (
                      <blockquote className={`border-l-2 pl-3 py-1 my-2 italic ${
                        darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'
                      }`}>
                        {children}
                      </blockquote>
                    ),
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    // Smart link component for URLs - internal domain links go to page analysis
                    a: ({ children, href }) => {
                      // Use domain from props (passed down from App through prop chain)
                      const isInternalLink = href && currentDomain && (() => {
                        try {
                          const linkDomain = new URL(href).hostname.replace(/^www\./, '');
                          const targetDomain = currentDomain.replace(/^www\./, '');
                          return linkDomain === targetDomain;
                        } catch {
                          // Fallback to string matching if URL parsing fails
                          return href.includes(currentDomain) || href.includes(`www.${currentDomain}`);
                        }
                      })();
                      
                      if (isInternalLink) {
                        // Internal domain link - navigate to page analysis
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent message expansion
                              // Navigate to page analysis instead of external site
                              navigate(`/page-analysis/${encodeURIComponent(href)}`);
                            }}
                            className={`break-words underline cursor-pointer ${
                              darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
                            }`}
                            title="View page analysis in QueryBurst"
                          >
                            {children} 📊
                          </button>
                        );
                      } else {
                        // External link - open in new tab
                        return (
                          <a 
                            href={href} 
                            className={`break-words ${
                              darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                            }`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            title="Open external link"
                          >
                            {children} ↗️
                          </a>
                        );
                      }
                    },
                    // Table components for data visualization
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-4 rounded-lg shadow-sm">
                        <table className={`min-w-full border-collapse text-xs ${
                          darkMode ? 'bg-gray-800' : 'bg-white'
                        }`}>
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className={
                        darkMode 
                          ? 'bg-gradient-to-r from-blue-900 to-blue-800' 
                          : 'bg-gradient-to-r from-blue-50 to-blue-100'
                      }>
                        {children}
                      </thead>
                    ),
                    tbody: ({ children }) => <tbody className="divide-y divide-gray-200 dark:divide-gray-700">{children}</tbody>,
                    tr: ({ children }) => (
                      <tr className={`transition-colors duration-150 ${
                        darkMode 
                          ? 'hover:bg-gray-750' 
                          : 'hover:bg-blue-25 even:bg-blue-25/50'
                      }`}>
                        {children}
                      </tr>
                    ),
                    th: ({ children }) => (
                      <th className={`px-4 py-3 text-left text-xs font-semibold tracking-wider uppercase ${
                        darkMode 
                          ? 'text-blue-200 border-b border-blue-700' 
                          : 'text-blue-900 border-b border-blue-200'
                      }`}>
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className={`px-4 py-3 text-xs whitespace-nowrap ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {children}
                      </td>
                    ),
                    // Enhanced markdown elements
                    input: ({ type, checked, ...props }) => {
                      if (type === 'checkbox') {
                        return (
                          <input
                            type="checkbox"
                            checked={checked}
                            readOnly
                            className={`mr-2 rounded border-2 ${
                              darkMode 
                                ? 'border-gray-600 bg-gray-700 text-blue-400' 
                                : 'border-gray-300 bg-white text-blue-600'
                            } focus:ring-2 focus:ring-blue-500`}
                            {...props}
                          />
                        );
                      }
                      return <input {...props} />;
                    },
                    // Custom components for enhanced styling
                    span: ({ children, className, ...props }) => {
                      // Badge styling for [!badge text]
                      if (className?.includes('badge')) {
                        return (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            darkMode 
                              ? 'bg-blue-900 text-blue-200 border border-blue-700' 
                              : 'bg-blue-100 text-blue-800 border border-blue-200'
                          }`} {...props}>
                            {children}
                          </span>
                        );
                      }
                      // Alert/callout styling for [!note], [!warning], etc.
                      if (className?.includes('alert')) {
                        const alertType = className.split('-')[1] || 'info';
                        const alertStyles = {
                          info: darkMode 
                            ? 'bg-blue-900/50 border-blue-700 text-blue-200' 
                            : 'bg-blue-50 border-blue-200 text-blue-800',
                          warning: darkMode 
                            ? 'bg-yellow-900/50 border-yellow-700 text-yellow-200' 
                            : 'bg-yellow-50 border-yellow-200 text-yellow-800',
                          success: darkMode 
                            ? 'bg-green-900/50 border-green-700 text-green-200' 
                            : 'bg-green-50 border-green-200 text-green-800',
                          error: darkMode 
                            ? 'bg-red-900/50 border-red-700 text-red-200' 
                            : 'bg-red-50 border-red-200 text-red-800'
                        };
                        return (
                          <div className={`border-l-4 p-3 my-3 rounded-r ${alertStyles[alertType] || alertStyles.info}`} {...props}>
                            {children}
                          </div>
                        );
                      }
                      return <span className={className} {...props}>{children}</span>;
                    },
                    // Horizontal rule styling
                                hr: () => (
                <hr className={`my-4 border-t-2 ${
                  darkMode ? 'border-gray-600' : 'border-gray-200'
                }`} />
              ),
              ul: ({ children }) => (
                <ul className={`space-y-2 my-4 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className={`space-y-2 my-4 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {children}
                </ol>
              ),
              li: ({ children, className, ...props }) => {
                const isCheckbox = typeof children === 'object' && children?.some?.(child => 
                  child?.type === 'input' && child?.props?.type === 'checkbox'
                );
                
                return (
                  <li 
                    className={`flex items-start gap-3 py-1 ${
                      isCheckbox 
                        ? 'items-center' 
                        : `relative pl-6 ${
                            darkMode 
                              ? 'before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:bg-blue-400 before:rounded-full before:transform before:scale-75' 
                              : 'before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:bg-blue-500 before:rounded-full before:transform before:scale-75'
                          }`
                    } ${className || ''}`}
                    {...props}
                  >
                    {isCheckbox ? (
                      <div className="flex items-center gap-2 w-full">
                        {children}
                      </div>
                    ) : (
                      <div className="flex-1 leading-relaxed">{children}</div>
                    )}
                  </li>
                );
              },
                    }}
                  >
                    {(() => {
                      const { content: displayContent, isTruncated } = getMessageDisplayContent(message.content, 800, portalMode, currentContext);
                      return displayContent; // Return only the string content for ReactMarkdown
                    })()}
                  </ReactMarkdown>
                  
                  {/* Inline buttons for property welcome messages */}
                  {(() => {
                    // console.log('🔍 [MessageBubble] Checking for inline buttons:', {
                    //   isUser,
                    //   showInlineButtons: message.showInlineButtons,
                    //   inlineButtons: message.inlineButtons,
                    //   buttonsDisabled: message.buttonsDisabled,
                    //   messageId: message.id
                    // });
                    return !isUser && message.showInlineButtons && message.inlineButtons && (
                      <div className="mt-4 flex gap-3">
                        {message.inlineButtons.map((button, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              // console.log('🔘 [MessageBubble] Button clicked:', { 
                              //   action: button.action, 
                              //   messageId: message.id, 
                              //   buttonsDisabled: message.buttonsDisabled,
                              //   hasOnInlineButtonClick: !!onInlineButtonClick
                              // });
                              if (!message.buttonsDisabled && onInlineButtonClick) {
                                onInlineButtonClick(button.action, message.id);
                              }
                            }}
                            disabled={message.buttonsDisabled}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              message.buttonsDisabled
                                ? darkMode
                                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : button.style === 'primary'
                                  ? darkMode
                                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                                  : darkMode
                                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 border border-gray-600'
                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                            }`}
                          >
                            {message.buttonsDisabled ? 'Processing...' : button.text}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  
                  {(() => {
                    // Don't show expand button for messages with inline buttons or disabled expansion
                    if (message.disableExpansion || message.showInlineButtons) {
                      return null;
                    }
                    
                    const { content: displayContent, isTruncated } = getMessageDisplayContent(message.content, 800, portalMode, currentContext);
                    return isTruncated ? (
                      <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                        <button
                          onClick={() => onExpandMessage(message)}
                          className={`inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
                            darkMode 
                              ? 'text-purple-200 hover:text-purple-100 bg-purple-800/20 hover:bg-purple-700/30' 
                              : 'text-purple-600 hover:text-purple-700 bg-purple-50 hover:bg-purple-100'
                          }`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                          </svg>
                          Read full message
                        </button>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
            
            {/* Legacy auto-expand indicator - no longer used but kept for compatibility */}
            {false && isAutoExpanding && (
              <div className={`
                mt-3 p-3 rounded-lg border-l-4 border-blue-500 animate-pulse
                ${darkMode ? 'bg-blue-900/20 text-blue-200' : 'bg-blue-50 text-blue-700'}
              `}>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-blue-400' : 'bg-blue-500'} animate-bounce`} style={{ animationDelay: '0ms' }} />
                    <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-blue-400' : 'bg-blue-500'} animate-bounce`} style={{ animationDelay: '150ms' }} />
                    <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-blue-400' : 'bg-blue-500'} animate-bounce`} style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm font-medium">
                    {message.autoExpandData?.content_preview || 'Opening in expanded view...'}
                  </span>
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </div>
                <div className="text-xs mt-1 opacity-75">
                  Reason: {message.autoExpandData?.reason || 'Detailed content for better readability'}
                </div>
              </div>
            )}
            
            {/* Report recommendation */}
            {message.reportRecommended && (
              <div className={`
                mt-2 p-2 rounded border-l-2 border-blue-500
                ${darkMode ? 'bg-gray-900' : 'bg-blue-50'}
              `}>
                <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  💡 Recommended Report
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {message.reportRecommended}
                </div>
              </div>
            )}
  
            {/* Navigation Action & Rationale */}
            {message.action === 'navigate_to_analysis' && message.rationale && (
              <div className={`
                mt-2 p-2 rounded border-l-2 border-green-500
                ${darkMode ? 'bg-gray-900' : 'bg-green-50'}
              `}>
                <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                  🎯 Filter Applied
                </div>
                <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {message.rationale}
                </div>
                <div className={`text-xs mt-1 font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                  Report: {message.actionData?.report_type || 'analysis'}
                </div>
              </div>
            )}
          </div>
          
          {/* Suggestions */}
          {message.suggestions && message.suggestions.length > 0 && (
            <div className="mt-2 space-y-1">
              {message.suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => onSuggestionClick(suggestion)}
                  className={`
                    block w-full text-left text-xs px-3 py-2 rounded-lg transition-all duration-200
                    ${darkMode 
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700 hover:border-purple-500' 
                      : 'bg-white text-gray-600 hover:bg-purple-50 border border-gray-200 hover:border-purple-300'
                    }
                  `}
                >
                  💬 {suggestion}
                </button>
              ))}
            </div>
          )}
          

          
          {/* Timestamp */}
          <div className={`
            text-xs mt-1 
            ${isUser ? 'text-right' : 'text-left'}
            ${darkMode ? 'text-gray-500' : 'text-gray-400'}
          `}>
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    );
  };

export default ChatSidebarMessageBubble;
